import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../utils/shad-cn-helper"
import { NoiseOverlay } from "./noise-overlay"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm lg:text-base font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='size-'])]:lg:size-5 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        golden:
          "relative bg-popover text-popover-foreground hover:bg-popover/90 border-2 opacity-100",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      color: {
        default: "",
        red: "text-red-800 hover:text-red-700 focus-visible:ring-red-300",
        blue: "text-blue-800 hover:text-blue-700 focus-visible:ring-blue-300",
        green:
          "text-green-800 hover:text-green-700 focus-visible:ring-green-300",
        yellow:
          "text-yellow-800 hover:text-yellow-700 focus-visible:ring-yellow-300",
      },
      size: {
        default:
          "h-8 px-3 py-2 has-[>svg]:px-2.5 sm:h-9 sm:px-4 sm:py-2 sm:has-[>svg]:px-3 lg:h-10 lg:px-6 lg:py-2 lg:has-[>svg]:px-4",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  children,
  color,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, color, className: cn(className, 'overflow-clip') }))}
      {...props}
    >
      {variant === "golden" && (
        <NoiseOverlay
          id="button-noise"
          opacity={25}
          baseFrequency={0.8}
        />
      )}
      {children}
    </Comp>
  );
}

export { Button, buttonVariants };
